import { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel, formatRangePretty, formatFYLabel } from "@/lib/useReportRange";
import { exportFinancialPDF, exportFinancialExcel } from "@/lib/exporters";
import YoYSnapshot from "@/components/YoYSnapshot";

// Stable references — defined once at module scope so they don't trigger child re-renders.
const CF_YOY_METRICS = [
  { label: "Net Cash from Operating", path: "operating.net_cash_from_operating", varKey: "net_cash_from_operating" },
  { label: "Net Cash from Investing", path: "investing.net_cash_from_investing", varKey: "net_cash_from_investing" },
  { label: "Net Cash from Financing", path: "financing.net_cash_from_financing", varKey: "net_cash_from_financing" },
  { label: "Net Change in Cash", path: "net_change_in_cash", varKey: "net_change_in_cash" },
];

function fmt(amount) {
  if (amount === 0 || amount == null) return "—";
  if (amount < 0) return `(${formatINR(Math.abs(amount))})`;
  return formatINR(amount);
}

function Row({ label, amount, bold, highlight, indent, italic, testid }) {
  return (
    <tr className={highlight ? "bg-emerald-50 border-t-2 border-emerald-300" : ""}>
      <td className={`py-1.5 pl-3 ${indent ? "pl-10" : ""} ${italic ? "italic text-gray-500" : ""} ${bold ? "font-semibold text-gray-900" : "text-gray-700"} ${indent ? "text-[11px]" : "text-xs"}`}>
        {label}
      </td>
      <td className={`text-right pr-3 font-mono tabular-nums ${bold ? "font-semibold text-gray-900" : "text-gray-700"} ${italic ? "text-gray-500" : ""} ${indent ? "text-[11px]" : "text-xs"}`} data-testid={testid}>
        {fmt(amount)}
      </td>
    </tr>
  );
}

function SectionHeader({ letter, title }) {
  return (
    <tr className="bg-blue-50 border-t-2 border-blue-300">
      <td className="py-2 pl-3 font-mono text-xs text-blue-900" style={{ width: 28 }}>{letter}</td>
      <td className="py-2 font-semibold text-blue-900 uppercase tracking-wider text-xs" colSpan={2}>{title}</td>
    </tr>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);

  // Stable params reference for YoYSnapshot so it doesn't refetch on every parent render.
  const yoyParams = useMemo(() => ({ start: applied.start, end: applied.end }), [applied.start, applied.end]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/companies/${activeId}/reports/cash-flow`, { params }).then((r) => setData(r.data));
  }, [activeId, applied.start, applied.end, params]);

  const onExcel = () => {
    if (!data) return;
    const wcA = (data.operating.working_capital_changes.assets || []).map((r) => ({ cells: [`    ${r.name}`, r.amount], type: "ledger" }));
    const wcL = (data.operating.working_capital_changes.liabilities || []).map((r) => ({ cells: [`    ${r.name}`, r.amount], type: "ledger" }));
    const invR = (data.investing.rows || []).map((r) => ({ cells: [`    ${r.name}`, r.amount], type: "ledger" }));
    const finR = (data.financing.rows || []).map((r) => ({ cells: [`    ${r.name}`, r.amount], type: "ledger" }));
    const adj = (data.operating.adjustments || []).map((a) => ({ cells: [`    ${a.label}`, a.amount], type: "ledger" }));
    const rows = [
      { cells: ["A.  Cash Flow from Operating Activities", ""], type: "group" },
      { cells: ["Net Profit before Tax", data.operating.net_profit_before_tax], type: "sub" },
      ...adj,
      { cells: ["Operating Profit before Working Capital changes", data.operating.operating_profit_before_wc], type: "total" },
      { cells: ["Changes in Working Capital — Current Assets", ""], type: "sub" },
      ...wcA,
      { cells: ["Changes in Working Capital — Current Liabilities", ""], type: "sub" },
      ...wcL,
      { cells: ["Net Cash from Operating Activities (A)", data.operating.net_cash_from_operating], type: "grand" },
      { cells: ["B.  Cash Flow from Investing Activities", ""], type: "group" },
      ...invR,
      { cells: ["Net Cash from Investing Activities (B)", data.investing.net_cash_from_investing], type: "grand" },
      { cells: ["C.  Cash Flow from Financing Activities", ""], type: "group" },
      ...finR,
      { cells: ["Net Cash from Financing Activities (C)", data.financing.net_cash_from_financing], type: "grand" },
      { cells: ["Net Change in Cash & Cash Equivalents (A + B + C)", data.net_change_in_cash], type: "total" },
      { cells: ["Add: Opening Cash & Cash Equivalents", data.opening_cash_and_equivalents], type: "sub" },
      { cells: ["Closing Cash & Cash Equivalents (computed)", data.computed_closing_cash], type: "total" },
      { cells: ["Closing Cash & Cash Equivalents (actual per books)", data.actual_closing_cash], type: "total" },
      { cells: ["Reconciliation Difference", data.reconciliation_difference], type: "sub" },
    ];
    exportFinancialExcel(`cash-flow-${active?.name || "report"}`, [
      { name: "Cash Flow", title: "Cash Flow Statement (Indirect Method)", headers: ["Particulars", "Amount (₹)"], rows },
    ], { company: active?.name, subtitle: `For the period ${formatRangeLabel(range)} — AS-3 / Ind AS 7` });
  };

  const onPDF = () => {
    if (!data) return;
    const wcA = (data.operating.working_capital_changes.assets || []).map((r) => ({ cells: [r.name, fmt(r.amount)], type: "ledger" }));
    const wcL = (data.operating.working_capital_changes.liabilities || []).map((r) => ({ cells: [r.name, fmt(r.amount)], type: "ledger" }));
    const invR = (data.investing.rows || []).map((r) => ({ cells: [r.name, fmt(r.amount)], type: "ledger" }));
    const finR = (data.financing.rows || []).map((r) => ({ cells: [r.name, fmt(r.amount)], type: "ledger" }));
    const adj = (data.operating.adjustments || []).map((a) => ({ cells: [a.label, fmt(a.amount)], type: "ledger" }));
    const rows = [
      { cells: ["A.  Cash Flow from Operating Activities", ""], type: "group" },
      { cells: ["Net Profit before Tax", fmt(data.operating.net_profit_before_tax)], type: "sub" },
      ...adj,
      { cells: ["Operating Profit before Working Capital changes", fmt(data.operating.operating_profit_before_wc)], type: "total" },
      { cells: ["Changes in Working Capital", ""], type: "sub" },
      ...wcA,
      ...wcL,
      { cells: ["Net Cash from Operating Activities (A)", fmt(data.operating.net_cash_from_operating)], type: "grand" },
      { cells: ["B.  Cash Flow from Investing Activities", ""], type: "group" },
      ...invR,
      { cells: ["Net Cash from Investing Activities (B)", fmt(data.investing.net_cash_from_investing)], type: "grand" },
      { cells: ["C.  Cash Flow from Financing Activities", ""], type: "group" },
      ...finR,
      { cells: ["Net Cash from Financing Activities (C)", fmt(data.financing.net_cash_from_financing)], type: "grand" },
      { cells: ["Net Change in Cash & Cash Equivalents", fmt(data.net_change_in_cash)], type: "total" },
      { cells: ["Add: Opening Cash & Cash Equivalents", fmt(data.opening_cash_and_equivalents)], type: "sub" },
      { cells: ["Closing Cash & Cash Equivalents (computed)", fmt(data.computed_closing_cash)], type: "total" },
      { cells: ["Closing Cash & Cash Equivalents (per books)", fmt(data.actual_closing_cash)], type: "total" },
    ];
    exportFinancialPDF(`cash-flow-${active?.name || "report"}`,
      "Cash Flow Statement",
      [{ title: "", headers: ["Particulars", "Amount (Rs.)"], rows }],
      {
        company: active?.name,
        address: active?.address,
        phone: active?.phone,
        email: active?.email,
        gstin: active?.gstin,
        pan: active?.pan,
        fy: formatFYLabel(range),
        subtitle: `${formatRangePretty(range)}  -  Indirect Method (AS-3 / Ind AS 7)`,
        date: applied.end,
        place: active?.state || "",
      });
  };

  return (
    <>
      <PageHeader
        title="Cash Flow Statement"
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}  •  Indirect Method (AS-3 / Ind AS 7)`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4" data-testid="cash-flow-page">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        {data && (
          <YoYSnapshot
            endpoint={`/companies/${activeId}/reports/cash-flow`}
            params={yoyParams}
            metrics={CF_YOY_METRICS}
            testid="cf-yoy"
          />
        )}
        {!data && <div className="text-sm text-gray-500" data-testid="cf-loading">Loading…</div>}
        {data && (
          <>
            <div className="border border-gray-200 rounded bg-white overflow-hidden">
              <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 text-xs font-semibold text-gray-900 uppercase tracking-wider">
                Cash Flow Statement
              </div>
              <table className="w-full" data-testid="cf-table">
                <thead>
                  <tr className="text-[10px] uppercase text-gray-500 border-b border-gray-200">
                    <th className="text-left pl-3 py-1.5 font-medium">Particulars</th>
                    <th className="text-right pr-3 py-1.5 font-medium w-44">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {/* A. Operating */}
                  <SectionHeader letter="A" title="Cash Flow from Operating Activities" />
                  <Row label="Net Profit before Tax" amount={data.operating.net_profit_before_tax} bold testid="cf-pbt" />
                  {data.operating.adjustments.map((a, i) => (
                    <Row key={`adj-${i}`} label={a.label} amount={a.amount} indent testid={`cf-adj-${i}`} />
                  ))}
                  <Row label="Operating Profit before Working Capital changes" amount={data.operating.operating_profit_before_wc} bold testid="cf-op-before-wc" />

                  <tr><td colSpan={2} className="pt-2 pl-3 text-[10px] uppercase tracking-wider text-gray-500">Adjustments for Working Capital changes</td></tr>
                  {data.operating.working_capital_changes.assets.map((r, i) => (
                    <Row key={`wca-${i}`} label={`Δ ${r.name}`} amount={r.amount} indent italic testid={`cf-wca-${i}`} />
                  ))}
                  {data.operating.working_capital_changes.liabilities.map((r, i) => (
                    <Row key={`wcl-${i}`} label={`Δ ${r.name}`} amount={r.amount} indent italic testid={`cf-wcl-${i}`} />
                  ))}
                  {data.operating.working_capital_changes.assets.length === 0 && data.operating.working_capital_changes.liabilities.length === 0 && (
                    <tr><td colSpan={2} className="pl-10 py-1.5 text-[11px] text-gray-400 italic">No working-capital movement in this period.</td></tr>
                  )}
                  <Row label="Net Cash from / (used in) Operating Activities (A)" amount={data.operating.net_cash_from_operating} bold highlight testid="cf-net-operating" />

                  {/* B. Investing */}
                  <SectionHeader letter="B" title="Cash Flow from Investing Activities" />
                  {data.investing.rows.length === 0 && (
                    <tr><td colSpan={2} className="pl-10 py-1.5 text-[11px] text-gray-400 italic">No investing activity in this period.</td></tr>
                  )}
                  {data.investing.rows.map((r, i) => (
                    <Row key={`inv-${i}`} label={r.name} amount={r.amount} indent italic testid={`cf-inv-${i}`} />
                  ))}
                  <Row label="Net Cash from / (used in) Investing Activities (B)" amount={data.investing.net_cash_from_investing} bold highlight testid="cf-net-investing" />

                  {/* C. Financing */}
                  <SectionHeader letter="C" title="Cash Flow from Financing Activities" />
                  {data.financing.rows.length === 0 && (
                    <tr><td colSpan={2} className="pl-10 py-1.5 text-[11px] text-gray-400 italic">No financing activity in this period.</td></tr>
                  )}
                  {data.financing.rows.map((r, i) => (
                    <Row key={`fin-${i}`} label={r.name} amount={r.amount} indent italic testid={`cf-fin-${i}`} />
                  ))}
                  <Row label="Net Cash from / (used in) Financing Activities (C)" amount={data.financing.net_cash_from_financing} bold highlight testid="cf-net-financing" />

                  {/* Summary */}
                  <tr className="bg-gray-100 border-t-2 border-gray-300">
                    <td colSpan={2} className="py-2 pl-3 font-semibold text-gray-900 uppercase tracking-wider text-xs">Reconciliation</td>
                  </tr>
                  <Row label="Net Increase / (Decrease) in Cash & Equivalents (A + B + C)" amount={data.net_change_in_cash} bold testid="cf-net-change" />
                  <Row label="Add: Opening Cash & Cash Equivalents" amount={data.opening_cash_and_equivalents} testid="cf-opening-cash" />
                  <Row label="Closing Cash & Cash Equivalents (computed)" amount={data.computed_closing_cash} bold testid="cf-closing-computed" />
                  <Row label="Closing Cash & Cash Equivalents (per books)" amount={data.actual_closing_cash} bold testid="cf-closing-actual" />
                  {Math.abs(data.reconciliation_difference) > 0.5 && (
                    <Row label="Reconciliation Difference" amount={data.reconciliation_difference} italic indent testid="cf-recon-diff" />
                  )}
                </tbody>
              </table>
            </div>

            <div className={`text-xs rounded border p-3 ${
              Math.abs(data.reconciliation_difference) < 0.5
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-800"
            }`} data-testid="cf-balance-check">
              {Math.abs(data.reconciliation_difference) < 0.5
                ? <>Reconciled: computed closing cash matches your books ✓</>
                : <>Difference of <span className="font-mono">₹ {formatINR(Math.abs(data.reconciliation_difference))}</span> between computed and actual closing cash — typically caused by direct cash income/expense ledgers not classified into Schedule III groups.</>
              }
            </div>

            <div className="text-[10px] text-gray-400 italic">
              Prepared under the Indirect Method as per AS-3 / Ind AS 7. Inventories use {(data?.valuation_method || "FIFO").replaceAll("_", " ")} valuation. Schedule III (Division I) classifications for Non-Corporate Entity.
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default function CashFlow() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
